import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { X } from 'lucide-react-native';
import { Modal } from 'react-native';

import { Box } from '@/components/ui/box';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';

type Props = {
  isOpen: boolean;
  /** True when the viewport is below the `md` breakpoint or on native. */
  isCompactLayout: boolean;
  onClose: () => void;
};

function PanelBody({ onClose }: { onClose: () => void }) {
  return (
    <VStack className="flex-1">
      {/* Bottom-sheet handle — native only */}
      {!isWeb && (
        <Box className="items-center py-2">
          <Box className="h-1 w-10 rounded-full bg-outline-300" />
        </Box>
      )}

      {/* Header — no title for now; content lands with a feature branch. */}
      <HStack className="h-20 items-center justify-end border-b border-outline-100 px-5">
        <Pressable
          hitSlop={8}
          onPress={onClose}
          className="rounded-md p-1.5 data-[hover=true]:bg-secondary-50 data-[active=true]:bg-secondary-100"
          {...getAccessibilityProps({ role: 'button', label: 'Close panel' })}>
          <Icon as={X} size="lg" className="text-secondary-500" />
        </Pressable>
      </HStack>

      {/* Empty placeholder — content is added by feature branches. */}
      <VStack className="flex-1 items-center justify-center px-6 py-10">
        <Text size="sm" className="text-center text-typography-400">
          Nothing here yet.
        </Text>
      </VStack>
    </VStack>
  );
}

/**
 * InboxSidebarPanel — the inbox right sidebar (shell only).
 *
 * Panel chrome, open/close behavior, and an empty placeholder — feature
 * branches fill it later. Web wide layout renders a ~360px full-height column
 * beside the chat thread; native / compact web renders a bottom-sheet Modal.
 */
export function InboxSidebarPanel({ isOpen, isCompactLayout, onClose }: Props) {
  if (isWeb && !isCompactLayout) {
    if (!isOpen) {
      return null;
    }

    return (
      <VStack
        className="w-96 flex-shrink-0 self-stretch border-l border-outline-200 bg-background-0 shadow-soft-2"
        role="complementary"
        aria-label="Insights panel">
        <PanelBody onClose={onClose} />
      </VStack>
    );
  }

  return (
    <Modal
      visible={isOpen}
      transparent
      animationType="slide"
      onRequestClose={onClose}
      {...(isWeb ? { 'aria-modal': true, role: 'dialog' } : { accessibilityViewIsModal: true })}>
      <Pressable
        className="flex-1 bg-black/40"
        onPress={onClose}
        {...getAccessibilityProps({ role: 'button', label: 'Close insights panel' })}
      />
      <VStack className="max-h-[70%] rounded-t-2xl bg-background-0 shadow-soft-3">
        <PanelBody onClose={onClose} />
      </VStack>
    </Modal>
  );
}
